// ============================================================================
// polygonGrid.ts — Precomputed exterior-polygon membership grid
//
// BETA 13.91 FPS — User's Performance recording showed 82% of frame time was
// spent in `pointInPolygon` (minified `_w`/`Sw`) inside `isWalkable()`.
// Hot path: per-enemy update → canMoveTo → 5× isWalkable → iterate ALL
// exterior green curves (~30 polygons × ~20 vertices each) → ray-cast test.
// At 50 enemies × 5 checks × 30 polys × 20 verts × 60fps = 9M polygon-vertex
// tests per second.
//
// THIS MODULE: build a 2D grid once per level (lazily, on first lookup) where
// each cell stores 1 if it falls inside any exterior green curve polygon, 0
// otherwise. Per-frame query becomes a single Uint8Array index lookup.
//
// Manus owns `pointInPolygon` in roomCollisionGrid.ts — we don't touch that.
// We just call it once per cell at level load, then never again at runtime.
// ============================================================================

import { pointInPolygon, type ExteriorGreenCurve } from './roomCollisionGrid';

/**
 * Cell size in world units. Smaller = more accurate near polygon edges but
 * larger memory + slower build. 32px on a 10000-unit world = ~313² = ~98K
 * cells, ~98 KB memory. Build cost ~50ms (only at level load).
 */
const CELL_SIZE = 32;

/** Module state — invalidated when curves array reference changes. */
let _grid: Uint8Array | null = null;
let _gridForCurves: ReadonlyArray<ExteriorGreenCurve> | null = null;
let _gridWorldSize = 0;
let _gridW = 0;

/**
 * Build the polygon-membership grid. Iterates each polygon's bounding box
 * (not the whole world per polygon) so cost scales with polygon coverage,
 * not world size.
 */
function buildGrid(
  curves: ReadonlyArray<ExteriorGreenCurve>,
  worldSize: number,
): Uint8Array {
  const gridW = Math.ceil(worldSize / CELL_SIZE);
  const grid = new Uint8Array(gridW * gridW);

  for (const curve of curves) {
    // Bounding box of this polygon
    let minX = Infinity, minY = Infinity;
    let maxX = -Infinity, maxY = -Infinity;
    for (const v of curve.polygon) {
      if (v.x < minX) minX = v.x;
      if (v.y < minY) minY = v.y;
      if (v.x > maxX) maxX = v.x;
      if (v.y > maxY) maxY = v.y;
    }

    // Bbox → cell range (clamped to grid bounds)
    const cx0 = Math.max(0, Math.floor(minX / CELL_SIZE));
    const cy0 = Math.max(0, Math.floor(minY / CELL_SIZE));
    const cx1 = Math.min(gridW - 1, Math.ceil(maxX / CELL_SIZE));
    const cy1 = Math.min(gridW - 1, Math.ceil(maxY / CELL_SIZE));

    // For each cell in the bbox, test the cell center against the polygon.
    // Skip cells already marked by another polygon (idempotent merge).
    for (let cy = cy0; cy <= cy1; cy++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        const i = cy * gridW + cx;
        if (grid[i]) continue;
        const wx = cx * CELL_SIZE + CELL_SIZE * 0.5;
        const wy = cy * CELL_SIZE + CELL_SIZE * 0.5;
        if (pointInPolygon(wx, wy, curve.polygon)) {
          grid[i] = 1;
        }
      }
    }
  }

  return grid;
}

/**
 * Ensure the cached grid matches the current curves + worldSize. Rebuilds
 * if curves array reference changed (new level) or worldSize changed.
 */
function ensureGrid(
  curves: ReadonlyArray<ExteriorGreenCurve>,
  worldSize: number,
): Uint8Array | null {
  if (!curves || curves.length === 0) return null;
  if (_grid && _gridForCurves === curves && _gridWorldSize === worldSize) {
    return _grid;
  }
  _grid = buildGrid(curves, worldSize);
  _gridForCurves = curves;
  _gridWorldSize = worldSize;
  _gridW = Math.ceil(worldSize / CELL_SIZE);
  return _grid;
}

/**
 * Fast O(1) check: is the world-space point (px, py) inside any of the
 * exterior green curve polygons?
 *
 * Replaces the per-call O(C × V) loop in isWalkable / getGreenZoneRedirect
 * (where C = number of polygons, V = vertices per polygon).
 *
 * Builds the grid lazily on first call after a level load.
 */
export function isInAnyExteriorPolygon(
  curves: ReadonlyArray<ExteriorGreenCurve> | undefined,
  worldSize: number,
  px: number,
  py: number,
): boolean {
  if (!curves || curves.length === 0) return false;
  const grid = ensureGrid(curves, worldSize);
  if (!grid) return false;

  const cx = Math.floor(px / CELL_SIZE);
  const cy = Math.floor(py / CELL_SIZE);
  if (cx < 0 || cx >= _gridW || cy < 0 || cy >= _gridW) return false;
  return grid[cy * _gridW + cx] === 1;
}

/**
 * Reset the cached grid. Call this when starting a new run or otherwise
 * invalidating the level. Optional — the lazy rebuild handles new curves
 * automatically when the array reference changes, so this is mostly a
 * memory hygiene call.
 */
export function resetPolygonGrid(): void {
  _grid = null;
  _gridForCurves = null;
  _gridWorldSize = 0;
  _gridW = 0;
}
